import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { usePageBreadcrumb, usePageSidebar, useStrapiApi, useStrapiParams } from '@/hooks';
import { sidebarItems } from '../sidebar';
import { ROUTES } from '@/constants';
import { getCategory, getPostsByCategory } from '@/services/strapi';
import type { Category, Post, StrapiListRequest, StrapiListResponse, StrapiResponse } from '@/interfaces/strapi';
import { PostCard } from '@/components';
import CTNPagination from '@ctn/ui/components/ctn-pagination';

export const ActualidadCategoria = (): ReactElement => {
	const initialPage = 1;
	const [page, setPage] = useState(initialPage);
	const pageSize = 6;
	const { category: categorySlug } = useParams<{ category: string }>();

	const catParams = useStrapiParams({
		page: 1,
		pageSize: 1,
		sort: 'id:desc',
		additionalFilters: categorySlug ? { slug: { $eq: categorySlug }, active: true } : { active: true },
	});

	const { data: categoryResponse, loading: loadingCat } = useStrapiApi<StrapiResponse<Category>, StrapiListRequest>(
		getCategory,
		{
			autoFetch: true,
			params: catParams,
		},
	);

	const category = categoryResponse?.data;

	const postsParams = useStrapiParams({
		page,
		pageSize,
		sort: 'publishedAt:desc',
		additionalFilters: categorySlug ? { web_categorias_noticia: { slug: { $eq: categorySlug } } } : undefined,
		populate: {
			image: true,
			file: true,
			web_categorias_noticia: true,
		},
	});

	const { data: postsResponse, loading: loadingPosts } = useStrapiApi<StrapiListResponse<Post>, StrapiListRequest>(
		getPostsByCategory,
		{
			autoFetch: true,
			params: postsParams,
		},
	);

	const posts = postsResponse?.list ?? [];
	const pagination = postsResponse?.pagination;
	const totalPages = pagination?.pageCount ?? 1;
	const paginationRef = useRef<any>(null);

	usePageBreadcrumb([{ label: 'Actualidad', to: ROUTES.ACTUALIDAD }, { label: category?.name ?? 'Categoría' }]);

	const sidebar = useMemo(() => sidebarItems, []);
	usePageSidebar(sidebar, true);

	useEffect(() => {
		if (pagination && paginationRef.current?.goToPage) {
			paginationRef.current.goToPage(pagination.page ?? page);
		}
		if (pagination && typeof pagination.page === 'number' && pagination.page !== page) {
			setPage(pagination.page);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pagination?.page]);

	const handlePageChange = (newPage: number) => {
		if (newPage === page) return;
		setPage(newPage);
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	return (
		<div>
			{loadingCat || loadingPosts ? (
				<p>Cargando…</p>
			) : posts.length === 0 ? (
				<p>No hay noticias en esta categoría.</p>
			) : (
				<>
					<div className="c-cnm-post__list o-grid">
						{posts.map((p) => (
							<PostCard key={p.id} categorySlug={categorySlug ?? ''} item={p} />
						))}
					</div>

					{pagination && totalPages > 1 && (
						<nav className="c-pagination u-mt-16" aria-label="Paginación">
							<CTNPagination
								ref={paginationRef}
								pageSize={pageSize}
								totalData={pagination.total ?? 0}
								locales={{
									text1: 'Página',
									text2: 'de',
									text3: 'Mostrando registros {0} de {1}',
								}}
								onPageChange={handlePageChange}
							/>
						</nav>
					)}
				</>
			)}
		</div>
	);
};
